/* ============================================================================
 * Raisy /pitchdeck — BIG VISION  ·  v2026.09.2 (D-060)
 *
 * "WE THINK BIG." — X+2 = X+1 (the co-founder's PDF, challenged) + a VISION
 * BLOCK. The spine is IMPORTED from `slidesV2` so the two decks cannot drift;
 * this file adds only the forward-looking slides, inserted AFTER the era slide
 * so the vision arrives before the product.
 *
 * NON-TECHNICAL BY RULE. No kernel / bot / BD / API vocabulary. The PO's notes
 * (2026-09-13) translated into business language:
 *   · human communication is slow — deals take months because people are the wire
 *   · until agents can fully prepare business deals, the founder prepares AND decides
 *   · companies will communicate with companies — a communication standard
 *   · every company has an input signature and an output signature (Yellow Pages)
 *   · humans become the gatekeepers; the platform prepares everything
 *   · the memory layer plugs into any AI — we back compliance + safety regulation
 *   · and it starts where knowledge is the product
 *
 * Grounding (deliberately NOT visible in the deck): RAISY_01_MANIFEST.md
 * (ENTITY · OFFERING · REQUIREMENT · CONTRACT · CAMPAIGN) and
 * `playano-docs/raisy/raisy_memory.md` (plug-and-play memory, MR19/D-046).
 * Funnel numbers: RAISY_PITCH_P3_DATA_FOR_PLOTS.md — Metal 2026 · Affinity 2026 ·
 * vcbeast 2026 [directional]. Sources are printed under every stat.
 * ========================================================================== */
import { motion } from 'framer-motion';
import { ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { CountUp, Kicker, Shell, Stat, fadeUp, stagger } from './slides';
import { DECK_SLIDES_V2, PRELOAD_V2 } from './slidesV2';

const EASEX = [0.22, 1, 0.36, 1] as const;

/* ========================================================================
 * VISION 1 · the premise — human communication is slow
 * ====================================================================== */
const SLOW = [
  { num: '01', title: 'Everyone you can reach', desc: 'the long list' },
  { num: '02', title: 'First meeting', desc: 'timing mismatch kills most' },
  { num: '03', title: 'Deep evaluation', desc: 'ghosted · no budget' },
  { num: '04', title: 'Diligence', desc: 'legal & tech friction' },
  { num: '05', title: 'Signed', desc: 'the 1–2% that survive', hero: true },
];

export function VHumanSlow({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Measuring the cost of slow…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Human communication is slow.
          <small>We make it faster.</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          A deal is two companies trying to understand each other — through people. Every
          email, every call, every follow-up is manual translation. It works. It costs months.
        </motion.p>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.2, ease: EASEX, delay: 0.8 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {SLOW.map((m, i) => (
              <motion.div
                key={m.title}
                className={cn('pd-jnode', m.hero && 'hero')}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASEX, delay: 0.9 + i * 0.28 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
          <div className="pd-jpath">
            Every stage in between is <b>a human doing admin</b> — and every handover loses time.
          </div>
        </motion.div>
        <motion.div
          className="pd-stats"
          style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 22 }}
          variants={stagger}
        >
          <Stat
            n={<CountUp to={57} duration={3.2} />}
            l="meetings to close a successful raise — a failed one stops at 14"
            s="Metal, 2026"
          />
          <Stat
            n={<CountUp to={80} duration={3.2} />}
            l="opportunities an investor reviews for one decision"
            s="Affinity, 2026"
          />
          <Stat
            n={<CountUp to={2} suffix="%" duration={3.2} />}
            l="of cold outreach ever becomes a term sheet"
            s="vcbeast, 2026 · directional"
          />
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * VISION 2 · until agents can fully prepare business deals
 * ====================================================================== */
export function VAgentsPrepare({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Until agents can prepare the whole deal…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The platform prepares everything.
          <small>The human decides.</small>
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Every step of a deal that is not a decision is preparation — research, outreach,
          follow-up, paperwork. Today the founder does all of it <i>and</i> the deciding.
          That is the part about to change.
        </motion.p>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: '1fr auto 1fr', marginTop: 26 }}
          variants={stagger}
        >
          <motion.div className="pd-vcard" variants={fadeUp}>
            <h4>Today — the founder</h4>
            <ul>
              <li>finds who to talk to, by hand</li>
              <li>writes every message, by hand</li>
              <li>chases every follow-up, by hand</li>
              <li>assembles every document, by hand</li>
              <li className="human">decides</li>
            </ul>
          </motion.div>
          <div className="pd-vs">becomes</div>
          <motion.div className="pd-vcard hot" variants={fadeUp}>
            <h4>Next — the platform</h4>
            <ul>
              <li>who to talk to, scored from public sources</li>
              <li>what to say, written for each one</li>
              <li>when to follow up, remembered exactly</li>
              <li>what has to be signed, prepared in advance</li>
              <li className="human">decides</li>
            </ul>
          </motion.div>
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          This is not a ten-year promise. It is what our customers already do on Raisy —
          one workflow at a time, with a person approving every send.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * VISION 3 · a communication standard between companies
 * ====================================================================== */
export function VStandard({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Writing the standard…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Companies will communicate with companies.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Today two companies can only talk <i>through people</i> — email, calls, meetings.
          Tomorrow they talk through a standard: what each side needs and what each side
          offers, stated once and kept current. People approve at the boundary; the standard
          carries the rest.
        </motion.p>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: '1fr auto 1fr', marginTop: 26 }}
          variants={stagger}
        >
          <motion.div className="pd-vnode" variants={fadeUp}>
            <span className="pd-vnode-t">Company A</span>
            <span className="pd-vnode-s">what it needs · what it offers</span>
          </motion.div>
          <motion.div className="pd-vstd" variants={fadeUp}>
            <b>the standard</b>
            <small>one common language for needs and offers</small>
          </motion.div>
          <motion.div className="pd-vnode" variants={fadeUp}>
            <span className="pd-vnode-t">Company B</span>
            <span className="pd-vnode-s">what it offers · what it needs</span>
          </motion.div>
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          Not a better inbox — <b>a common language</b>. That is the piece nobody has built.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * VISION 4 · input / output signature — the Yellow Pages economy
 * ====================================================================== */
export function VSignatures({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Indexing what companies need and offer…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Every company has an input signature and an output signature.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          What it needs, and what it offers. Today that information is scattered across
          websites, decks, inboxes and people's heads — so the only way to find a fit is to
          talk to everyone. With signatures, the fit is known before anyone writes an email.
        </motion.p>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: '1fr 1fr', marginTop: 24 }}
          variants={stagger}
        >
          <motion.div className="pd-vsig" variants={fadeUp}>
            <span className="pd-vsig-t">Input · what it needs</span>
            <p>the capital, the tool, the partner, the licence, the person</p>
          </motion.div>
          <motion.div className="pd-vsig out" variants={fadeUp}>
            <span className="pd-vsig-t">Output · what it offers</span>
            <p>the product, the service, the capacity, the expertise, the capital</p>
          </motion.div>
        </motion.div>
        <motion.div className="pd-vmatch" variants={fadeUp}>
          <b>a fit you can act on</b> — surfaced before the first email is written
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          Like the Yellow Pages — except the directory can also negotiate.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * VISION 5 · the gatekeeper economy
 * ====================================================================== */
const HUMAN_KEEPS = [
  { t: 'Judgement', d: 'whether this is the right deal at all' },
  { t: 'Trust and relationship', d: 'the part that is personal, and stays personal' },
  { t: 'The signature', d: 'who is accountable when it is done' },
];

export function VGatekeepers({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Giving the decision back…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          Humans become the gatekeepers.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          The platform prepares the deal: who to talk to, what to say, what it costs, what the
          terms should be, what has to be signed. People decide whether to make it — and own
          the yes.
        </motion.p>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 26 }}
          variants={stagger}
        >
          {HUMAN_KEEPS.map((k) => (
            <motion.div key={k.t} className="pd-vcard hot" variants={fadeUp}>
              <h4>{k.t}</h4>
              <p className="pd-vbody">{k.d}</p>
            </motion.div>
          ))}
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          Everything else becomes the platform's job. <b>A sale that took two years becomes a
          decision that takes weeks.</b>
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * VISION 6 · memory on any AI — and the ethics that come with it
 * ====================================================================== */
const MEMORY = [
  { t: 'It remembers', d: 'what was agreed, what was tried, what was rejected — nobody repeats themselves' },
  { t: 'It shows why', d: 'every answer points back to the source it came from' },
  { t: 'It never acts alone', d: 'anything that leaves the company waits for a person’s yes' },
];

export function VTrust({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Making the memory accountable…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          The memory that makes AI useful is the one that makes it accountable.
          <small>And it plugs into any AI system.</small>
        </motion.h1>
        <motion.div
          className="pd-vgrid"
          style={{ gridTemplateColumns: 'repeat(3,1fr)', marginTop: 24 }}
          variants={stagger}
        >
          {MEMORY.map((m) => (
            <motion.div key={m.t} className="pd-vcard" variants={fadeUp}>
              <h4>{m.t}</h4>
              <p className="pd-vbody">{m.d}</p>
            </motion.div>
          ))}
        </motion.div>
        <motion.p className="pd-vsay" variants={fadeUp}>
          We can put this on any model — plug and play. That position lets us back the rules
          being written for AI, on <b>compliance and safety</b>, instead of working around them.
        </motion.p>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * VISION 7 · where it starts (the audience's own market)
 * ====================================================================== */
const EDU = [
  { num: '01', title: 'Schools', desc: 'needs tools and teachers · offers places' },
  { num: '02', title: 'Universities', desc: 'needs partners and funding · offers research' },
  { num: '03', title: 'Training providers', desc: 'needs programmes · offers certifications' },
  { num: '04', title: 'Education companies', desc: 'needs distribution · offers platforms' },
  { num: '05', title: 'Publishers', desc: 'needs adoption · offers content', hero: true },
];

export function VEdtech({ dir }: { dir: number }) {
  return (
    <Shell custom={dir}>
      <motion.div variants={stagger} initial="hidden" animate="show">
        <Kicker>Starting where knowledge is the product…</Kicker>
        <motion.h1 className="pd-h1" variants={fadeUp}>
          We believe education is the first market where this changes everything.
        </motion.h1>
        <motion.p className="pd-sub" variants={fadeUp}>
          Schools, universities, training providers, education companies and publishers buy
          and sell constantly — programmes, content, licences, tools, partnerships, people.
          It is a market built on relationships and long cycles, and most of that time is
          preparation.
        </motion.p>
        <motion.div className="pd-journey" variants={fadeUp}>
          <div className="pd-jline">
            <motion.i
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 2.2, ease: EASEX, delay: 0.8 }}
              style={{ transformOrigin: 'left' }}
            />
          </div>
          <div className="pd-jrow">
            {EDU.map((m, i) => (
              <motion.div
                key={m.title}
                className={cn('pd-jnode', m.hero && 'hero')}
                initial={{ opacity: 0, y: 26 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASEX, delay: 0.9 + i * 0.28 }}
              >
                <div className="pd-jdot" />
                <div className="pd-jnum">{m.num}</div>
                <div className="pd-jtitle">{m.title}</div>
                <div className="pd-jdesc">{m.desc}</div>
              </motion.div>
            ))}
          </div>
          <div className="pd-jpath">
            Give every one of them <b>an input and an output signature</b> and the slowest
            part of every deal disappears.
          </div>
        </motion.div>
      </motion.div>
    </Shell>
  );
}

/* ========================================================================
 * THE REGISTRY — X+1 with the vision block inserted after the era slide.
 *
 *   [title, hook, hook-reveal, era]  +  VISION 1–7  +  [Company OS … team]
 *
 * Splice, never copy: the spine stays one definition (SSOT), so a fix to a
 * shared slide lands in every version at once.
 * ====================================================================== */
export const DECK_SLIDES_VISION: Array<(p: { dir: number }) => ReactElement> = [
  ...DECK_SLIDES_V2.slice(0, 4),
  VHumanSlow,
  VAgentsPrepare,
  VStandard,
  VSignatures,
  VGatekeepers,
  VTrust,
  VEdtech,
  ...DECK_SLIDES_V2.slice(4),
];

/** Same assets as X+1 — the vision slides draw their visuals, they load nothing new. */
export const PRELOAD_VISION = PRELOAD_V2;
